import { useEffect, useState } from 'react';
import { drillScore } from '@core/scoring/drill';
import type { ScoreSource } from '../engine/useDrillSession';
import type { DrillResult, ItemResult, OrgConfig } from '../engine/types';
import type { DrillDoc } from '../engine/types';

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * The end of an attempt. The score shown here is the server's — the client never
 * computes one — so what a rep sees is what the certification record says.
 */
export function AttemptSummary({
  drill, result, source, config, onDone,
}: {
  drill: DrillDoc;
  result: DrillResult;
  source: ScoreSource;
  config: OrgConfig;
  onDone: () => void;
}) {
  // The score is on screen immediately, but the ladder behind this screen only
  // moves when the server writes stageProgress. Leaving before then would show a
  // rung the rep has just cleared as though they had not, so the way back waits
  // for the record — while they read their results, which is where the wait
  // costs nothing. A stalled write must never trap anyone, so it gives up
  // waiting after a few seconds and lets them through regardless.
  const [waivedWait, setWaivedWait] = useState(false);
  useEffect(() => {
    if (source !== 'preview') return;
    const timer = setTimeout(() => setWaivedWait(true), 8000);
    return () => clearTimeout(timer);
  }, [source]);
  const saving = source === 'preview' && !waivedWait;
  /**
   * The server never came back.
   *
   * `source` stays 'preview' until the scoring trigger writes `result`, so this
   * is the state where a rep has a score on screen and no record behind it —
   * and the ladder will not move, because the ladder is derived from
   * `stageProgress`, which only the server writes.
   *
   * Letting them out quietly was the wrong half of the trade: they leave, the
   * rung they just cleared still says Start, and the only available conclusion
   * is that the app lost their work or the pass did not count. Saying so costs
   * nothing and points at the one thing that actually fixes it.
   */
  const unrecorded = source === 'preview' && waivedWait;

  const score = drillScore(result);
  const missed = result.itemResults.filter((r: ItemResult) => !r.correct);
  const redFlagged = result.redFlagMisses ?? [];
  // §6.8 — a red-flag miss fails the stage independent of the aggregate. When
  // that is why a rep failed, the score alone would be actively misleading.
  const failedOnRedFlagAlone = !result.passed
    && redFlagged.length > 0
    && score >= result.threshold;

  return (
    <main className="summary">
      <div className={`verdict ${result.passed ? 'pass' : 'fail'}`}>
        <h1>{result.passed ? 'Passed' : 'Not yet'}</h1>
        <p className="score">{pct(score)}</p>
        <p className="muted">
          {result.correctCount} of {result.itemsScored} exactly right
          {result.itemsUnanswered > 0 && ` · ${result.itemsUnanswered} unanswered`}
          {' · '}pass mark {pct(result.threshold)}
          {result.boundaryItemsScored > 0 && ` · ${result.boundaryItemsScored} counted double`}
        </p>

        {failedOnRedFlagAlone && (
          <p className="redflag-note" role="alert">
            Your score cleared the pass mark. {redFlagged.length === 1 ? 'One answer' : `${redFlagged.length} answers`}
            {' '}did not, and {redFlagged.length === 1 ? 'it is' : 'they are'} an automatic fail whatever the total.
          </p>
        )}
        {unrecorded && (
          <p className="redflag-note" role="alert">
            This result has not been recorded yet, so your progress will not move
            on until it is. Your answers are saved — nothing has been lost, and
            nothing needs doing again. If this keeps happening, the scoring
            function may not be deployed.
          </p>
        )}
        {source === 'diverged' && (
          <p className="small warn" role="alert">
            This is the score on your record. What you saw a moment ago differed —
            your manager has been able to see the recorded result either way.
          </p>
        )}
        {result.severeConfusionCount > 0 && (
          <p className="warn">
            {result.severeConfusionCount} of your misreads would have led the call badly
            in the wrong direction.
          </p>
        )}
      </div>

      {redFlagged.length > 0 && (
        <section className="review">
          <h2>Answers that fail on their own</h2>
          <ul className="review-list">
            {redFlagged.map((miss) => (
              <li key={miss.itemId} className="review-item sev-severe">
                <span className="review-label">
                  {miss.selected === null
                    ? 'Left unanswered'
                    : <>You said <strong>{label(miss.selected, config)}</strong></>}
                </span>
                <span className="pill pill-severe">{miss.topic.replace(/_/g, ' ')}</span>
              </li>
            ))}
          </ul>
          <p className="small muted">
            These come straight from your organization's own compliance standard.
            Getting one wrong fails the stage on its own.
          </p>
        </section>
      )}

      {missed.length > 0 && (
        <section className="review">
          <h2>What to look at</h2>
          <ul className="review-list">
            {missed.filter((r: ItemResult) => !r.redFlag).map((r: ItemResult) => (
              <li key={r.itemId} className={`review-item sev-${r.severity ?? 'none'}`}>
                <span className="review-label">
                  You said <strong>{label(r.selected, config)}</strong>
                  {' · '}it was <strong>{label(r.preferred, config)}</strong>
                </span>
                {r.acceptable && <span className="pill pill-passed">defensible</span>}
                {r.severity && <span className={`pill pill-${r.severity}`}>{r.severity}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <button className="primary wide" onClick={onDone} disabled={saving}>
        {saving
          ? 'Saving your result…'
          : result.passed ? 'Back to your progress' : `Try ${drill.title} again`}
      </button>
    </main>
  );
}

function label(id: string | null, config: OrgConfig): string {
  if (!id) return 'nothing';
  return config.personas.find((p) => p.id === id)?.name ?? id.replace(/_/g, ' ');
}
